import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, MoreHorizontal, Trash2 } from "lucide-react";

import { ResidentLinkCard } from "@/components/trust_suite/ResidentLinkCard";
import { residentCreateDefaultsFromPermit } from "@/components/trust_suite/residentLinkDefaults";
import type { ResidentSummary } from "@/api/residents";
import {
  type PermitApplication,
  type PermitApplicationPayload,
  type PermitApplicationStatus,
  PERMIT_APPLICATION_STATUS_LABELS,
  PERMIT_TYPE_LABELS,
  type PermitType,
} from "@/api/permit_applications";
import { listPermitApplicationDocuments } from "@/api/permit_application_documents";
import { PermitApplicationActivity } from "@/components/permit_suite/PermitApplicationActivity";
import { PermitApplicationDocuments } from "@/components/permit_suite/PermitApplicationDocuments";
import { PermitApplicationTasks } from "@/components/permit_suite/PermitApplicationTasks";
import { PermitApplicationTeamFields } from "@/components/permit_suite/PermitApplicationTeamFields";
import { DetailNotesCard } from "@/components/workflow_tracking/DetailNotesCard";
import {
  formatPermitDeadline,
  permitApplicationStatusLabel,
  permitApplicationStatusPillClass,
  permitDeadlineSummary,
  permitDeadlineTone,
} from "@/components/permit_suite/permitSuiteDisplay";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import DatePicker from "@/components/ui/date-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

type PermitApplicationDetailProps = {
  municipalityId: number;
  fileViewerEnabled: boolean;
  application: PermitApplication;
  onUpdate: (patch: Partial<PermitApplicationPayload>) => void;
  onRequestDelete: () => void;
  updating: boolean;
};

function parseDeadline(value: string | null): Date | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

export function PermitApplicationDetail({
  municipalityId,
  fileViewerEnabled,
  application,
  onUpdate,
  onRequestDelete,
  updating,
}: PermitApplicationDetailProps) {
  const [notesDraft, setNotesDraft] = useState(application.notes ?? "");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    setNotesDraft(application.notes ?? "");
  }, [application.id, application.notes]);

  const { data: documents = [] } = useQuery({
    queryKey: ["permit_application_documents", municipalityId, application.id],
    queryFn: () => listPermitApplicationDocuments(municipalityId, application.id),
    enabled: fileViewerEnabled && activeTab === "files",
  });

  const notesDirty = notesDraft.trim() !== (application.notes ?? "").trim();
  const deadlineTone = permitDeadlineTone(application.deadline_at);

  const saveNotes = () => {
    const next = notesDraft.trim();
    if (next !== (application.notes ?? "").trim()) {
      onUpdate({ notes: next || null });
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="permit-application-detail">
      <div className="shrink-0 border-b bg-background px-4 py-4 sm:px-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium",
                  permitApplicationStatusPillClass(application.status),
                )}
              >
                {permitApplicationStatusLabel(application.status)}
              </span>
              <Badge variant="secondary" className="font-normal">
                {PERMIT_TYPE_LABELS[application.permit_type]}
              </Badge>
            </div>
            <h2 className="text-lg font-semibold leading-snug tracking-tight sm:text-xl">
              {application.title}
            </h2>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>Updated {formatRelativeTime(application.updated_at)}</span>
              {application.application_number ? (
                <span>#{application.application_number}</span>
              ) : null}
              {application.applicant_name ? <span>{application.applicant_name}</span> : null}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Permit actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={onRequestDelete}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete permit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {deadlineTone === "warning" && application.deadline_at ? (
          <Alert variant="destructive" className="mt-4">
            <CalendarClock className="h-4 w-4" />
            <AlertTitle>Deadline attention</AlertTitle>
            <AlertDescription>{permitDeadlineSummary(application.deadline_at)}</AlertDescription>
          </Alert>
        ) : null}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 border-b px-4 py-2 sm:px-6">
          <TabsList className="h-9 w-full justify-start sm:w-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {fileViewerEnabled ? (
              <TabsTrigger value="files" className="gap-1.5">
                Files
                {documents.length > 0 ? (
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px]">
                    {documents.length}
                  </Badge>
                ) : null}
              </TabsTrigger>
            ) : null}
            <TabsTrigger value="activity" className="gap-1.5">
              Activity
            </TabsTrigger>
          </TabsList>
        </div>

        <ScrollArea className="min-h-0 flex-1" viewportClassName="min-w-0">
          <TabsContent value="overview" className="m-0 px-4 py-4 sm:px-6">
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Application status</CardTitle>
                  <CardDescription>
                    Track where this permit sits in your review workflow.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="permit-detail-status">Status</Label>
                    <Select
                      value={application.status}
                      onValueChange={(value) =>
                        onUpdate({ status: value as PermitApplicationStatus })
                      }
                      disabled={updating}
                    >
                      <SelectTrigger id="permit-detail-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PERMIT_APPLICATION_STATUS_LABELS).map(([key, label]) => (
                          <SelectItem key={key} value={key}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <PermitApplicationTeamFields
                    municipalityId={municipalityId}
                    application={application}
                    onUpdate={onUpdate}
                    updating={updating}
                  />

                  <Separator />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Permit type</Label>
                      <Select
                        value={application.permit_type}
                        onValueChange={(value) => onUpdate({ permit_type: value as PermitType })}
                        disabled={updating}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PERMIT_TYPE_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Application number</Label>
                      <Input
                        defaultValue={application.application_number ?? ""}
                        disabled={updating}
                        onBlur={(event) => {
                          const value = event.target.value.trim() || null;
                          if (value === (application.application_number ?? null)) return;
                          onUpdate({ application_number: value });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Applicant</Label>
                      <Input
                        defaultValue={application.applicant_name ?? ""}
                        disabled={updating}
                        onBlur={(event) => {
                          const value = event.target.value.trim() || null;
                          if (value === (application.applicant_name ?? null)) return;
                          onUpdate({ applicant_name: value });
                        }}
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Project address</Label>
                      <Input
                        defaultValue={application.project_address ?? ""}
                        disabled={updating}
                        onBlur={(event) => {
                          const value = event.target.value.trim() || null;
                          if (value === (application.project_address ?? null)) return;
                          onUpdate({ project_address: value });
                        }}
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label>Review deadline</Label>
                    <DatePicker
                      value={parseDeadline(application.deadline_at)}
                      onChange={(date: Date | undefined) =>
                        onUpdate({ deadline_at: date ? date.toISOString() : null })
                      }
                      disabled={updating}
                    />
                    <p
                      className={cn(
                        "text-xs",
                        deadlineTone === "warning"
                          ? "text-amber-700 dark:text-amber-400"
                          : "text-muted-foreground",
                      )}
                    >
                      {permitDeadlineSummary(application.deadline_at)}
                    </p>
                    {application.deadline_at ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-muted-foreground"
                        disabled={updating}
                        onClick={() => onUpdate({ deadline_at: null })}
                      >
                        Clear deadline
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-4">
                <DetailNotesCard
                  id="permit-detail-notes"
                  value={notesDraft}
                  onChange={setNotesDraft}
                  onSave={saveNotes}
                  dirty={notesDirty}
                  saving={updating}
                  placeholder="Inspection requirements, conditions, next steps…"
                />

                <ResidentLinkCard
                  municipalityId={municipalityId}
                  residentId={application.resident_id}
                  resident={application.resident as ResidentSummary | null | undefined}
                  updating={updating}
                  onLink={(residentId) => onUpdate({ resident_id: residentId })}
                  createDefaults={residentCreateDefaultsFromPermit(application)}
                  createDialogTitle="Create resident from permit"
                  createDialogDescription="Creates a profile from this permit's applicant info and links it to this permit."
                />

                <Card>
                  <CardContent className="pt-6">
                    <PermitApplicationTasks
                      municipalityId={municipalityId}
                      applicationId={application.id}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <DetailRow
                      label="Status"
                      value={permitApplicationStatusLabel(application.status)}
                    />
                    <DetailRow
                      label="Owner"
                      value={application.assigned_to_name ?? "Unassigned"}
                    />
                    <DetailRow
                      label="Reviewers"
                      value={
                        application.reviewers?.length
                          ? String(application.reviewers.length)
                          : "None"
                      }
                    />
                    <DetailRow
                      label="Deadline"
                      value={formatPermitDeadline(application.deadline_at)}
                    />
                    <DetailRow
                      label="Added"
                      value={formatRelativeTime(application.created_at)}
                    />
                    <DetailRow
                      label="Last updated"
                      value={formatRelativeTime(application.updated_at)}
                    />
                    {application.project_address ? (
                      <DetailRow label="Address" value={application.project_address} />
                    ) : null}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {fileViewerEnabled ? (
            <TabsContent value="files" className="m-0 min-w-0 px-4 py-4 sm:px-6">
              <PermitApplicationDocuments
                municipalityId={municipalityId}
                applicationId={application.id}
                embedded
              />
            </TabsContent>
          ) : null}

          <TabsContent value="activity" className="m-0 px-4 py-4 sm:px-6">
            <PermitApplicationActivity
              municipalityId={municipalityId}
              applicationId={application.id}
              fetchEnabled={activeTab === "activity"}
            />
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </div>
  );
}
